import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import usePopover from "components/common/usePopover";
import AppointmentService from "modules/appointments/service/AppointmentService";
import PatientService from "../service/PatientService";
import usePrescriptionDoctors from "../helper/usePrescriptionDoctors";
import {
  docIdLabel,
  formatDocDate,
  formatDocTime,
  formatLabel,
  formatTone,
  isPrescriptionDoc,
  openDocument,
} from "../helper/documentConstants";

/**
 * "Appointment documents · View all" — the files attached to THIS appointment
 * (Figma 3015:4458, pill 5125:4565).
 *
 * NOT the same list as the header's other "Documents" control, which is every
 * document the PATIENT holds, grouped into folders. This one is
 * appointment-scoped and flat: an appointment carries a handful of files, and a
 * folder grid over two of them is noise. AppointmentDetails already shows the
 * same data as a flat list off the same endpoint.
 *
 * WHY A SIBLING OF DocumentsMenu RATHER THAN A PROP ON IT. The two differ in
 * their service, their response shape, their empty state and their whole body —
 * DocumentsMenu's grid is built from `normalizeDocGroups`, which this response
 * has no folders for. Parameterising would have left one component with two
 * disjoint halves.
 *
 * THE WIRE (api-appointment-service models/document.go, controllers/document.go):
 *   · GET /v1/appointment/document/{id} -> { appointment, documents[], count }
 *   · 204 NO CONTENT when nothing is mapped — the transport hands that to
 *     onResponse as `undefined`, so the `(res && res.documents) || []` guard is
 *     load-bearing, not defensive noise.
 *   · `count` is NOT documents.length: the server adds one for a legacy
 *     `appointment.reportURL` it does not return in the array. Anything shown to
 *     the user is counted off the array.
 *   · `format` is the numeric FileFormat enum, exactly as the patient-documents
 *     wire sends it, so formatLabel/formatTone apply unchanged.
 *   · There is no uploadedBy/createdBy on this response — the row shows the
 *     date only, and does not invent an author.
 */

/** Figma's chevron, matching the sibling control's trigger exactly. */
const ChevronDown = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 11 6"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M1 1L5.5 4.6 10 1" />
  </svg>
);
ChevronDown.propTypes = { className: PropTypes.string };

const ChevronRight = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 6.1 10.6"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M0.8 0.8L5.3 5.3L0.8 9.8" />
  </svg>
);
ChevronRight.propTypes = { className: PropTypes.string };

const CloseIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M7 7L17 17M17 7L7 17" />
  </svg>
);
CloseIcon.propTypes = { className: PropTypes.string };

const AppointmentDocumentsMenu = ({ appointmentId }) => {
  const [open, setOpen] = useState(false);
  const { triggerRef, menuRef, menuStyle } = usePopover(open, () => setOpen(false));
  const service = useRef(new AppointmentService()).current;
  /* The doc token lives on PatientService — see the mint below. */
  const patientService = useRef(new PatientService()).current;

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const loadedRef = useRef(false);

  const load = useCallback(() => {
    if (!appointmentId) return;
    setLoading(true);
    setError(false);
    service.getAppointmentDocMapping(
      appointmentId,
      (res) => {
        if (!mounted.current) return;
        loadedRef.current = true;
        /* 204 arrives as undefined. That is "none mapped", not a failure. */
        setDocs((res && res.documents) || []);
        setLoading(false);
      },
      () => {
        if (!mounted.current) return;
        /* Deliberately NOT swallowed into an empty list. A cross-tenant or
           unknown appointment answers 500 here, and rendering that as "no
           documents" would tell a doctor a permission problem is a fact about
           the patient. AppointmentDetails makes exactly that mistake. */
        setError(true);
        setLoading(false);
      }
    );
  }, [appointmentId, service]);

  useEffect(() => {
    if (!open) return;
    if (!loadedRef.current) load();
    /* Mint the doc token in the background so a row click can build the
       tokenised URL synchronously — window.open inside an async callback is
       what popup blockers eat. This control cannot free-ride on the sibling
       "Documents" menu having been opened first: without a token buildDocUrl
       silently returns the bare URL and the file host rejects it, which looks
       like a broken document rather than a missing token. */
    patientService.generateDocToken(
      () => {},
      () => {}
    );
  }, [open, load, patientService]);

  const files = docs || [];
  /* Each prescription PDF's doctor, for its row's label — looked up for the patient the PDF
     itself belongs to (each document carries its patientId), so this control needs no patient
     passed in. Fetched once, when the list opens on a prescription. */
  const rxDoc = files.find(isPrescriptionDoc);
  const doctorNames = usePrescriptionDoctors(rxDoc && rxDoc.patientId, open && !!rxDoc);
  /* The row list runs edge to edge (.is-files); the three message states want
     the body's normal 20px inset, so the modifier is conditional. */
  const showRows = !loading && !error && files.length > 0;

  const body = () => {
    if (loading) return <p className="ph-doc-msg">Loading documents&hellip;</p>;
    if (error) {
      return (
        <p className="ph-doc-msg">
          Couldn&rsquo;t load documents.{" "}
          <button type="button" className="ph-doc-retry" onClick={load}>
            Retry
          </button>
        </p>
      );
    }
    if (!files.length) {
      return <p className="ph-doc-msg">No documents uploaded for this appointment.</p>;
    }
    return (
      <div className="ph-doc-files">
        {files.map((d) => (
          /* The same row as DocumentsMenu's: a CONTAINER with the open action as
             the button inside it. The row's styles moved onto .ph-doc-open when
             that menu grew a per-row edit pencil, so a bare <button
             class="ph-doc-row"> here fell back to the browser's default button —
             grey fill, border, centred system font. This list has no editor, so
             the open button is the only child (see .ph-doc-open:only-child). */
          <div className="ph-doc-row" key={d.id}>
            <button type="button" className="ph-doc-open" onClick={() => openDocument(d)}>
              <span className={`ph-doc-badge is-${formatTone(d.format)}`}>
                {formatLabel(d.format)}
              </span>
              {/* Id over title, date over time — the same row as DocumentsMenu's
                  (Figma 3023:9415). `id` is the document's own id
                  (models/document.go Document.ID), not the mapping's, which this
                  response never carries; a prescription's carries its doctor. */}
              <span className="ph-doc-label">
                <span className="ph-doc-id">{docIdLabel(d, doctorNames)}</span>{" "}
                <span className="ph-doc-name">{d.title || "Untitled"}</span>
              </span>
              <span className="ph-doc-when">
                <span className="ph-doc-date">{formatDocDate(d.createdAt || d.updatedAt)}</span>{" "}
                <span className="ph-doc-time">{formatDocTime(d.createdAt || d.updatedAt)}</span>
              </span>
              <ChevronRight className="ph-doc-chev" />
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className="ph-doc-all"
        aria-haspopup="dialog"
        aria-expanded={open}
        /* Kept enabled on an empty result: the board draws no count, so the
           only way to know it is empty is to open it, and a control that
           disables itself for a reason the doctor cannot see reads as broken. */
        disabled={!appointmentId}
        onClick={() => setOpen((o) => !o)}
      >
        View all
        <ChevronDown className="ph-doc-all-chev" />
      </button>

      {open &&
        ReactDOM.createPortal(
          /* Portalled because `.ph-fold-inner` is `overflow: hidden` for the
             collapse animation, and the pinned header additionally sets
             opacity 0 / pointer-events none on the fold — either alone would
             swallow an inline popover. */
          <div
            ref={menuRef}
            className="ph-doc-menu"
            style={{ top: menuStyle.top, left: menuStyle.left }}
            role="dialog"
            aria-label="Appointment documents"
          >
            <div className="ph-doc-menu-head">
              <span className="ph-doc-menu-title">Appointment documents</span>
              {showRows && <span className="ph-doc-menu-count">&middot; {files.length}</span>}
              <button
                type="button"
                className="ph-doc-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                <CloseIcon className="ph-doc-menu-close-icon" />
              </button>
            </div>
            <div className={`ph-doc-menu-body${showRows ? " is-files" : ""}`}>{body()}</div>
          </div>,
          document.body
        )}
    </>
  );
};

AppointmentDocumentsMenu.propTypes = {
  /** The appointment whose mapped documents to list. */
  appointmentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default AppointmentDocumentsMenu;
